/**
 * Lets the browser's back button close a popup, the way a phone app's does. Holding the button
 * pushes a history entry with the page's own url, so Back pops that entry instead of leaving the
 * page, and the popstate it fires calls `onBack`.
 *
 * Holds stack: each gets its own entry, and Back closes only the newest. Releasing a hold whose
 * popup closed some other way (its close button, Esc, the backdrop) takes its entry back off, so
 * the next Back does something visible. An entry that cannot be taken off because something else
 * was pushed above it is left behind, and skipped over when Back reaches it.
 *
 * A hold made while one of those `history.back()` calls is still in flight waits for it to land
 * before pushing: the traversal is asynchronous, and a push in the meantime would be what it pops.
 * A push from anywhere else in the meantime (the page navigating as a popup closes) cancels the
 * traversal outright, and no popstate ever comes, so the wait gives up after BACK_TIMEOUT.
 */
const KEY = 'backButtonClose';
const BACK_TIMEOUT = 500;

let nextId = 1;
// Live holds, oldest first: { id, onBack, pushed }.
let holds = [];
let pendingBacks = 0;
let pendingTimer = null;
let listening = false;

function entryId(state) {
	return (state && state[KEY]) || 0;
}

function goBack() {
	pendingBacks += 1;
	clearTimeout(pendingTimer);
	pendingTimer = setTimeout(() => {
		pendingBacks = 0;
		pushWaiting();
	}, BACK_TIMEOUT);
	window.history.back();
}

function push(hold) {
	hold.pushed = true;
	window.history.pushState({ [KEY]: hold.id }, '');
}

function pushWaiting() {
	holds.filter((hold) => !hold.pushed).forEach(push);
}

function onPopState(event) {
	if(pendingBacks > 0) {
		pendingBacks -= 1;
		if(pendingBacks === 0) {
			clearTimeout(pendingTimer);
		}
	}
	const current = entryId(event.state);
	const popped = holds.filter((hold) => hold.pushed && hold.id > current);
	holds = holds.filter((hold) => !popped.includes(hold));
	popped.reverse().forEach((hold) => hold.onBack());

	if(current && !holds.some((hold) => hold.id === current)) {
		// An entry whose popup has already gone, left by a release that could not take it off.
		goBack();
		return;
	}
	if(pendingBacks === 0) {
		pushWaiting();
	}
}

/**
 * Starts listening for Back on behalf of a popup that has just opened. Returns `release`, to call
 * once it has closed by any route; calling it after Back has already closed it, or twice, is fine.
 */
export function holdBackButton(onBack) {
	if(!listening) {
		window.addEventListener('popstate', onPopState);
		listening = true;
	}
	const hold = { id: nextId++, onBack, pushed: false };
	holds.push(hold);
	if(pendingBacks === 0) {
		push(hold);
	}

	return function release() {
		if(!holds.includes(hold)) {
			return;
		}
		holds = holds.filter((other) => other !== hold);
		if(hold.pushed && entryId(window.history.state) === hold.id) {
			goBack();
		}
	};
}
